import app from 'flarum/forum/app';
import Icon from 'flarum/common/components/Icon';

/**
 * v0.18.18 (辉哥 10/19 拍板) — 需求一 · 未登录 (A 态) / 无权限 (B 态) 提示页
 *
 * ## 设计稿
 *   /tmp/scheduler-design-20261019/01-A-未登录-桌面1440.png (A 态)
 *   /tmp/scheduler-design-20261019/02-B-无权限-桌面1440.png   (B 态, **辉哥已逐条改稿**)
 *   文案以 设计说明.md 为准, 视觉参数取自现网既有类 (`.ZS-mc-card` / `.ZS-cal-event` …),
 *   图标用站点已有的 Font Awesome 7 Free, **不新增图标字体**。
 *
 * ## 🔴 三条辉哥拍板的硬性改动 (跟设计稿不一样, 以这里为准)
 *   1. **一个英文权限键都不许出现**。设计稿里 `canManageCards` / `canViewStats` 那一排灰锁项
 *      已删; 现在只列**中文功能名** (直接复用 tabList 里的 `name`)。
 *   2. **不留「复制管理员联系方式」按钮**。改成一行文案 `请联系你的教练或校区管理员开通`,
 *      且**不写任何具体联系方式** (企微 / 手机 / 邮箱 / 二维码一律没有) ——
 *      理由: 这页面会被分享到微信群, 写上联系方式等于公开泄露。
 *   3. 主 CTA 改成 `先回论坛首页`。
 *
 * ## 为什么配色**不用红色**
 *   红色在本站是报错色 (`.Alert--error` 一族)。A 态用蓝紫 = 「你还没登录, 自助可解」,
 *   B 态用琥珀 = 「你登录了, 但要找人开通」。设计稿刻意这么分的, 别改回红色。
 *
 * ## 为什么保留完整 tab 条而不是只给一张卡片
 *   设计稿的总原则: 页面要看起来「这是个真实存在、只是你暂时进不去的区域」,
 *   而不是「出错了」—— 用户要能一眼排除 404 / 系统故障。
 *   ⇒ 页头 + `排课中心` 标识 + **完整 11 个 tab** 全留着, 只是置灰不可点。
 *   (v0.18.45 加了「仪表盘」tab ⇒ 10 → 11; 本组件的 tab 条直接吃 buildTabList(), 无需另改。)
 *
 * ## B 态的 tab 条是**部分可点**的 (设计稿是全灰, 这里按需放宽)
 *   设计稿假设用户一项权限都没有, 所以全灰。但需求书写明「有权限的 tab 正常渲染,
 *   只有用户无权访问的那个 tab 才显示 B 态」—— 只勾了「我的卡片」的用户访问
 *   `/scheduler/rooms` 时, 他应该能从这一页直接点回「我的卡片」。
 *   ⇒ `isTabOpen(t.key)` 为真的 tab 保持可点。零权限用户看到的仍然与设计稿一致 (全灰)。
 *
 * ## ⛔ 安全: 本组件**永远不渲染任何 tab 内容**
 *   它只渲染 tab **名字与图标**。数据一行都不碰。
 *   真正的内容门是 SchedulerIndexPage 的 `state !== 'allow'` 分支 + v0.17.27 的 `tabPermission()`。
 *
 * ## v0.18.28 (辉哥 10/20 指示) — **A 态 (未登录) 瘦身**
 *   A 态去掉: `.ZS-gate-toprow` / `.ZS-gate-tabs` / `.ZS-gate-body` /
 *   `.ZS-gate-return` / `.ZS-gate-note` / `.ZS-gate-foot` 六块。
 *   A 态现在只剩: 图标 + 药丸 + 标题 + CTA 四个元素。
 *   ⇒ `currentTabLabel()` 随 `.ZS-gate-return` 一起删掉 (它只有那一个调用点)。
 *   **B 态 (已登录无权限) 一个字未改** —— 顶行 / tab 条 / body / note 全部保留。
 */
export default class SchedulerGatePage {
  constructor(vnode) {
    this.state = vnode.attrs.state; // 'guest' | 'denied' | 'license'
    this.gate = vnode.attrs.gate; // 服务端 payload['schedulerGate']
    this.tabs = vnode.attrs.tabs; // 全部 11 个 tab (未按权限过滤)
    this.isTabOpen = vnode.attrs.isTabOpen; // (tabKey) => boolean
    this.onSelectTab = vnode.attrs.onSelectTab;
    // v1.5 — 「站点标识已复制」的反馈标记 (C 态)
    this.copied = false;
  }
  oncreate() {
    // v0.18.18 — 未登录 (A 态) 时把浏览器标题也带上「需要登录」,
    //   免得用户把这一页收藏了之后标签页上还是站点名。已登录的 B 态保持原有标题。
    if (this.state === 'guest') {
      app.setTitle(this.t('gate-a-title'));
    }
    // v1.5 — 授权未生效 (C 态) 同理: 标题要写明「授权未生效」,
    //   否则运营方收藏这一页后, 标签页上只剩站点名, 根本看不出这是个授权问题。
    if (this.state === 'license') {
      app.setTitle(this.t('gate-c-title'));
    }
  }

  /**
   * 取一条 locale 文案, **保证返回字符串**。
   *
   * ## ⚠️ 为什么不能直接把 `trans()` 的结果当字符串用
   *   Flarum 2.0 的 `app.translator.trans()` 返回的是**数组** (本地实测:
   *   `typeof === 'object'`, `constructor.name === 'Array'`, 长度 1)。
   *   它按 `{placeholder}` 切段, 带参数时每一段是一个 vnode。
   *   本组件的 key 一个 placeholder 都没有 ⇒ 恒为 `["整句字符串"]`, join('') 就是原文。
   *   ⇒ 直接 `.split('**')` 会炸 (`split is not a function`), 本页曾因此整页白屏。
   *   万一将来有人在这些 key 里加了 `{placeholder}`, `every(字符串)` 会判假,
   *   原样把 vnode 数组交给 mithril 渲染 —— 仍然正确, 只是下面的加粗切分对它不生效。
   */
  t(key) {
    const v = app.translator.trans('ziven-scheduler.forum.' + key);
    if (!Array.isArray(v)) return v;
    return v.every(x => typeof x === 'string') ? v.join('') : v;
  }

  /** 把文案里的 `**…**` 渲染成 <strong>。markdown 在 JSX 文本节点里不会自己渲染。 */
  emphasis(text) {
    if (typeof text !== 'string') return text;
    return text.split('**').map((chunk, i) => i % 2 === 1 ? m("strong", {
      key: i
    }, chunk) : m("span", {
      key: i
    }, chunk));
  }

  /**
   * tab 条。A 态全锁; B 态按 isTabOpen 决定哪个能点。
   *
   * ⛔ v0.18.28 (辉哥 10/20 指示) — **A 态 (未登录) 不再渲染 tab 条**,
   *   顶行 (`.ZS-gate-toprow`) 同样不渲染。本方法与 topRow() 现在**只在 B 态**被调用
   *   (见 view())。A 态原有的唯一调用点 (`.ZS-gate-return` 里的 currentTabLabel) 已删除。
   */
  tabBar() {
    const guest = this.state === 'guest';
    return m("div", {
      className: "ZS-gate-tabs"
    }, m("div", {
      className: "ZS-slide-tab-bar"
    }, this.tabs.map(t => {
      const open = !guest && this.isTabOpen(t.key);
      return m("div", Object.assign({
        className: 'ZS-slide-tab' + (open ? '' : ' ZS-slide-tab--locked'),
        "data-k": t.key,
        key: t.key
      }, open ? {
        onclick: e => {
          // 与 normalView 的 tab 条同款: 挡掉 mobile tap 的内置 scroll-into-view
          // (v0.6.22e 的结论, 见 SchedulerIndexPage 同款注释)
          e.preventDefault();
          this.onSelectTab(t.key);
        }
      } : {}), m("i", {
        className: 'ZS-tab-icon fas ' + t.icon,
        style: {
          color: t.color
        }
      }), m("span", {
        className: "ZS-tab-label"
      }, t.name));
    })));
  }

  /**
   * 顶部 `排课中心` 标识行 (替代正常态的校区下拉)
   *
   * ⛔ v0.18.28 (辉哥 10/20 指示) — 现在**只在 B 态渲染** (A 态已去掉顶行)。
   *   下面 `denied` 分支因此恒为 true, 保留是为了让方法自身不依赖调用方前提。
   */
  topRow() {
    const denied = this.state === 'denied';
    return m("div", {
      className: "ZS-gate-toprow"
    }, m("span", {
      className: 'ZS-gate-badge' + (denied ? ' ZS-gate-badge--denied' : '')
    }, m(Icon, {
      name: "fas fa-snowflake"
    }), this.t(denied ? 'gate-badge-denied' : 'gate-badge-guest')), m("span", {
      className: "ZS-gate-topsub"
    }, this.t('gate-badge-guest-sub')));
  }

  /**
   * 打开站点自己的登录 / 注册弹窗。
   *
   * ## ⚠️ 为什么是弹窗, 不是 `href="/login"` —— 本地实测, 别按「Flarum 有 /login 页面」想当然
   *   设计稿与需求书都写「走站点已有的登录弹窗（`/login`，带 ?redirect=）」, 但实测:
   *     `GET  /login`     → **405** Method Not Allowed
   *     `GET  /register`  → **405**
   *     `GET  /signup`    → **404**（ziven-private-facade 注册的同路径路由当前未生效）
   *     `app.routes` 里 `login|register|signup` 一个都没有（本地 35 条路由实测）
   *   原因: Flarum 2.0 的 `Forum/routes.php` 只把 `/login` `/register` 注册成 **POST**
   *   （API 端点），**没有 GET 页面路由**; 站内所有「登录/注册」入口都是 `<button>` 弹窗
   *   （`HeaderSecondary.js` 里直接 `app.modal.show(...)` 一个 lazy chunk）。
   *   ⇒ 直接链到 `/login?redirect=…` 会给访客一个 **405 错误页**, 那是把一个拦截页
   *      换成另一个拦截页。所以这里走弹窗, `href` 只作降级兜底。
   *
   * ⚠️ 这段注释里**故意**不写出带引号的动态 import 字面量（下面那句「纯正则改写」就是原因）:
   *   flarum-webpack-config 的 `autoChunkNameLoader` 是**纯正则**改写源码,
   *   它的模式只跳过 `@xxx` 开头的 JSDoc 行, 注释正文里任何形如
   *   「import + 左括号 + 引号」的字面量都会被它当成真的动态 import 改写,
   *   结果 webpack 解析炸掉 (本仓已踩过一次)。要引 core 源码就只写模块名, 别带引号。
   *
   * ## 渐进增强 (为什么仍然是 `<a href>` 而不是 `<button onclick>`)
   *   - JS 在 ⇒ 弹窗, 跟站内其它登录入口完全一致, 不用离开当前页。
   *   - JS 不在 / 弹窗 chunk 加载失败 ⇒ 原生导航到服务端算好的、已过
   *     `SafeRedirectPath` 校验的 `loginUrl`。那一刻 redirect 参数才真正派上用场。
   *   ⇒ `loginUrl` / `registerUrl` 不是死代码, 它就是这条兜底路径。
   *
   * @param {'LogInModal'|'SignUpModal'} chunk
   * @returns {Promise<boolean>} true = 弹窗已打开; false = 打开失败, 调用方走原生跳转
   */
  async openAuthModal(chunk) {
    try {
      // 用 `flarum.reg.asyncModuleImport` 而不是静态 import ——
      //   Flarum 2.0 把 LogInModal / SignUpModal 打成了 lazy chunk, 静态 import 拿到的是
      //   undefined (与本仓 index.js 里 NotificationGrid 那段注释同款踩坑记录)。
      const mod = await flarum.reg.asyncModuleImport('flarum/forum/components/' + chunk);
      const Comp = mod && (mod.default || mod) || null;
      if (!Comp) throw new Error('empty module: ' + chunk);
      app.modal.show(Comp, {
        onsubmit: () => {
          // 登录成功后不必跳转: 用户本来就在这个 URL 上。
          // `m.redraw()` 会重跑 SchedulerIndexPage.view() → effectiveGate() 的逃生口
          // 看到 `app.session.user` 已经在了 ⇒ 放行, 切到正常视图。
          // 万一账号还没被授予排课权限, 重绘后会自然落到 B 态 —— 那正是应该看到的。
          m.redraw();
        }
      });
      return true;
    } catch (e) {
      console.warn('[ziven-scheduler] ' + chunk + ' 打开失败, 回退到原生链接', e);
      return false;
    }
  }
  authCta(kind) {
    const isLogin = kind === 'login';
    const href = isLogin ? this.gate.loginUrl || '/login' : this.gate.registerUrl || '/register';
    return m("a", {
      className: 'ZS-gate-btn ' + (isLogin ? 'ZS-gate-btn--primary' : 'ZS-gate-btn--ghost'),
      href: href,
      onclick: e => {
        e.preventDefault();
        this.openAuthModal(isLogin ? 'LogInModal' : 'SignUpModal').then(ok => {
          if (!ok) window.location.href = href; // 弹窗起不来 ⇒ 原生导航
        });
      }
    }, m(Icon, {
      name: isLogin ? 'fas fa-right-to-bracket' : 'fas fa-user-plus'
    }), this.t(isLogin ? 'gate-a-cta-login' : 'gate-a-cta-register'));
  }

  // ── A 态 · 未登录 (访客) ──
  /**
   * v0.18.28 (辉哥 10/20 指示) — A 态卡片**瘦身**。
   *
   * 删掉的 4 块: `p.ZS-gate-body` / `div.ZS-gate-return` / `div.ZS-gate-note` / `div.ZS-gate-foot`。
   * ⇒ 现在 A 态只留: 图标 `.ZS-gate-icon` + 药丸 `.ZS-gate-pill` + 标题 `.ZS-gate-title`
   *   + CTA `.ZS-gate-cta` (登录/注册两个按钮) 四块。
   *
   * ⚠️ 对应的 locale key (gate-a-body / gate-a-return-label / gate-a-return-note /
   *   gate-a-note / gate-a-footer / gate-a-footer-link) **故意保留在 YAML 里不动** ——
   *   删 locale 有翻译回退风险, 留着只是没人读, 无害。
   *   (B 态的 `.ZS-gate-body` / `.ZS-gate-note` 仍在, 用的是 gate-b-* 那批 key。)
   */
  guestCard() {
    return m("div", {
      className: "ZS-gate ZS-gate--guest"
    }, m("div", {
      className: "ZS-gate-icon ZS-gate-icon--guest"
    }, m(Icon, {
      name: "fas fa-user-lock"
    })), m("div", {
      className: "ZS-gate-pill"
    }, this.t('gate-badge-guest'), " \xB7 ", this.t('gate-badge-guest-sub')), m("h1", {
      className: "ZS-gate-title"
    }, this.t('gate-a-title')), m("div", {
      className: "ZS-gate-cta"
    }, this.authCta('login'), this.authCta('register')));
  }

  /** 功能开关矩阵 —— ⛔ 只有中文功能名, 一个权限键都没有 */
  featureMatrix() {
    return m("div", {
      className: "ZS-gate-matrix"
    }, m("div", {
      className: "ZS-gate-matrix-title"
    }, m(Icon, {
      name: "fas fa-shield-halved"
    }), this.t('gate-b-matrix-title')), m("div", {
      className: "ZS-gate-matrix-grid"
    }, this.tabs.map(t => {
      const on = this.isTabOpen(t.key);
      return m("div", {
        className: 'ZS-gate-matrix-item' + (on ? ' is-on' : ''),
        key: t.key
      }, m(Icon, {
        name: on ? 'fas fa-lock-open' : 'fas fa-lock'
      }), m("span", {
        className: "ZS-gate-matrix-name"
      }, t.name), m("span", {
        className: "ZS-gate-matrix-state"
      }, this.t(on ? 'gate-b-matrix-on' : 'gate-b-matrix-off')));
    })));
  }

  // ── B 态 · 已登录但没有该 tab 的权限 ──
  deniedCard() {
    const username = this.gate.username;
    // 「已开通 / 未开通」分开讲 —— 需求书要求把「你已经有的」和「你没有的」区分开。
    // 零开通 ⇒ 设计稿原句; 部分开通 ⇒ 换一句, 不谎报「一项都没授予」。
    const grantedCount = this.tabs.filter(t => this.isTabOpen(t.key)).length;
    const bodyKey = grantedCount === 0 ? 'gate-b-body-none' : 'gate-b-body-partial';
    return m("div", {
      className: "ZS-gate ZS-gate--denied"
    }, m("div", {
      className: "ZS-gate-icon ZS-gate-icon--denied"
    }, m(Icon, {
      name: "fas fa-user-shield"
    })), m("div", {
      className: "ZS-gate-pill ZS-gate-pill--denied"
    }, this.t('gate-badge-denied')), m("h1", {
      className: "ZS-gate-title"
    }, this.t('gate-b-title')), m("p", {
      className: "ZS-gate-body"
    }, this.emphasis(this.t(bodyKey))), m("div", {
      className: "ZS-gate-account"
    }, m("span", {
      className: "ZS-gate-account-avatar"
    }, username ? username.slice(0, 1).toUpperCase() : '?'), m("span", {
      className: "ZS-gate-account-main"
    }, m("span", {
      className: "ZS-gate-account-name"
    }, username), m("span", {
      className: "ZS-gate-account-sub"
    }, this.t('gate-b-logged-in-as'))), m("span", {
      className: "ZS-gate-account-tag"
    }, this.t('gate-b-status'))), this.featureMatrix(), m("ol", {
      className: "ZS-gate-steps"
    }, m("li", null, this.t('gate-b-step-1-a'), m("strong", null, this.t('gate-b-step-1-b')), m("strong", null, username), this.t('gate-b-step-1-c')), m("li", null, this.emphasis(this.t('gate-b-step-2'))), m("li", null, this.t('gate-b-step-3'))), m("div", {
      className: "ZS-gate-contact"
    }, m(Icon, {
      name: "fas fa-comment-dots"
    }), m("span", null, this.t('gate-b-contact'))), m("div", {
      className: "ZS-gate-cta"
    }, m("a", {
      className: "ZS-gate-btn ZS-gate-btn--primary",
      href: "/"
    }, m(Icon, {
      name: "fas fa-house"
    }), this.t('gate-b-cta-home'))), m("div", {
      className: "ZS-gate-note"
    }, m(Icon, {
      name: "fas fa-circle-info"
    }), m("span", null, this.t('gate-b-note'))));
  }

  // ── C 态 · 授权未生效（商业授权门控，v1.5）──
  /**
   * v1.5 — 「授权未生效」提示页。
   *
   * ## 为什么单独做一页，而不是继续用 core 的白标错误页
   *   core 的错误页只显示一句「扩展 … 未获得授权或授权已失效。」——
   *   运营方看不出**该找谁**、**要拿什么**去处理。
   *   ⇒ 这一页把「站点标识」摆出来（运营方复制给供应商即可），
   *     并写清三步操作。
   *
   * ## ⚠️ `siteId` 只在已登录时下发（服务端判的，见 SchedulerIndexController）
   *   访客看不到这一行 —— 它是站点标识，公开页面上到处贴不合适。
   *
   * ## ⛔ 本组件**永远不渲染任何 tab 内容**
   *   与 A / B 态同款：只渲染提示，不碰任何排课数据。
   */
  licenseCard() {
    const license = this.gate && this.gate.license || {};
    const siteId = license.siteId || '';
    const reason = license.reason || '';
    return m("div", {
      className: "ZS-gate ZS-gate--license"
    }, m("div", {
      className: "ZS-gate-icon ZS-gate-icon--license"
    }, m(Icon, {
      name: "fas fa-shield-halved"
    })), m("div", {
      className: "ZS-gate-pill ZS-gate-pill--license"
    }, this.t('gate-c-pill')), m("h1", {
      className: "ZS-gate-title"
    }, this.t('gate-c-title')), m("p", {
      className: "ZS-gate-body"
    }, this.emphasis(this.t('gate-c-body'))), reason ? m("div", {
      className: "ZS-gate-reason"
    }, m(Icon, {
      name: "fas fa-circle-info"
    }), m("span", null, reason)) : null, siteId ? m("div", {
      className: "ZS-gate-siteid"
    }, m("div", {
      className: "ZS-gate-siteid-label"
    }, this.t('gate-c-site-id-label')), m("div", {
      className: "ZS-gate-siteid-row"
    }, m("code", null, siteId), m("button", {
      type: "button",
      className: "ZS-gate-siteid-copy",
      title: this.t('gate-c-copy'),
      onclick: () => {
        // ⛔ clipboard API 在非 https / 无权限时可能不存在 ⇒ 可选链 + 兜底提示。
        const done = () => {
          this.copied = true;
          m.redraw();
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(siteId).then(done, () => {});
        }
      }
    }, m(Icon, {
      name: this.copied ? 'fas fa-circle-check' : 'fas fa-copy'
    }), m("span", null, this.t(this.copied ? 'gate-c-copied' : 'gate-c-copy')))), m("div", {
      className: "ZS-gate-siteid-hint"
    }, this.t('gate-c-site-id-hint'))) : null, m("ol", {
      className: "ZS-gate-steps"
    }, m("li", null, this.t('gate-c-step-1')), m("li", null, this.t('gate-c-step-2')), m("li", null, this.t('gate-c-step-3'))), m("div", {
      className: "ZS-gate-cta"
    }, m("a", {
      className: "ZS-gate-btn ZS-gate-btn--primary",
      href: "/"
    }, m(Icon, {
      name: "fas fa-house"
    }), this.t('gate-c-cta-home'))), m("div", {
      className: "ZS-gate-note"
    }, m(Icon, {
      name: "fas fa-circle-info"
    }), m("span", null, this.t('gate-c-note'))));
  }
  view() {
    // ── v0.18.28 (辉哥 10/20 指示) — A 态 (未登录) 去掉顶行 + tab 条 ──
    //   A 态现在只剩一张居中的门禁卡; B 态 (已登录无权限) **一字未动**,
    //   仍然渲染顶行 + 完整 tab 条 (设计稿要求: 让用户排除「404 / 系统故障」)。
    //
    //   写法说明: 用 `? ... : null` 而不是 `&& ...` —— 两者在 mithril 里对 `false`
    //   都会跳过渲染, 但 `: null` 语义上更明确, 且不依赖渲染器对 boolean 子节点的处理。
    const denied = this.state === 'denied';
    // v1.5 — C 态 (授权未生效) 与 A 态同款: 居中一张卡, 不渲染顶行与 tab 条。
    //   理由: 授权未生效时**整个排课中心都不可用**, 摆一条「看起来能用但点不动」的
    //   tab 条反而误导 —— 用户会以为是自己没权限。
    const license = this.state === 'license';
    return m("div", {
      className: "ZivenSchedulerRoot"
    }, denied ? this.topRow() : null, denied ? this.tabBar() : null, m("div", {
      className: "ZS-tab-content ZS-tab-content--gate"
    }, license ? this.licenseCard() : denied ? this.deniedCard() : this.guestCard()));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/SchedulerGatePage', SchedulerGatePage);